import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import type { AxiosError } from 'axios'

import { ValidationErrors } from 'store/types'

import { getAxiosInstance } from 'services/api'

import { Comment, MediumPost, News, NewsDetails } from 'types/app'

import { CommentParams, NewsParams } from './types'

const api = getAxiosInstance()

export interface PaginationParams {
  page?: number
  limit?: number
  sort?: string
  order?: 'asc' | 'desc'
  userId?: string
  /** Explore sort tab: 'new' (default) | 'hot' | 'top' | 'boosted' */
  filter?: 'new' | 'hot' | 'top' | 'boosted'
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export const getAllNews = createAsyncThunk<
  News[],
  void,
  { rejectValue: ValidationErrors }
>('news/getAllNews', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/all/news')
    return response.data.news
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

// Paginated news fetch for better performance
export const getPaginatedNews = createAsyncThunk<
  PaginatedResponse<News>,
  PaginationParams,
  { rejectValue: ValidationErrors }
>('news/getPaginatedNews', async (params = {}, { rejectWithValue }) => {
  try {
    const { page = 1, limit = 10, sort = 'createdAt', order = 'desc', userId, filter } = params
    const response = await api.get('/all/news/paginated', {
      params: { page, limit, sort, order, ...(userId && { userId }), ...(filter && { filter }) }
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// export const getAllNewsWithKeyword = createAsyncThunk<
//   News[],
//   string,
//   { rejectValue: ValidationErrors }
// >('news/getAllNewsWithKeyword', async (keyword, { rejectWithValue }) => {
//   try {
//     const response = await api.get(`/all/news?game_id=${keyword}`)
//     return response.data.news
//   } catch (err) {
//     const error = err as AxiosError<ValidationErrors>

//     if (!error.response) throw err

//     return rejectWithValue(error.response.data)
//   }
// })

export const getNewsDetails = createAsyncThunk<
  NewsDetails,
  { newsId: string },
  { rejectValue: ValidationErrors }
>('news/getNewsDetail', async ({ newsId }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/news/details/${newsId}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const createNews = createAsyncThunk<
  News,
  NewsParams,
  { rejectValue: ValidationErrors }
>('news/create', async ({ image, title, body, poll }, { rejectWithValue }) => {
  try {
    const params = new FormData()
    if (image) params.append('image', image)
    if (title) params.append('title', title)
    params.append('body', body)

    // Add poll data if present
    if (poll) {
      params.append('poll', JSON.stringify(poll))
    }

    const response = await api.post('/create/news', params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})
export const updateNews = createAsyncThunk<
  News,
  NewsParams,
  { rejectValue: ValidationErrors }
>(
  'news/update',
  async ({ image, title, body, newsId }, { rejectWithValue }) => {
    try {
      const params = new FormData()
      if (image) params.append('image', image)
      if (title) params.append('title', title)
      params.append('body', body)

      const response = await api.put(`/news/${newsId}`, params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const deleteNews = createAsyncThunk<
  void,
  string,
  { rejectValue: ValidationErrors }
>('news/delete', async (newsId, { rejectWithValue }) => {
  try {
    await api.delete(`/news/${newsId}`)
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const likeNews = createAsyncThunk<
  News,
  { userId: string; newsId: string },
  { rejectValue: ValidationErrors }
>('news/like', async ({ userId, newsId }, { rejectWithValue }) => {
  try {
    const params = new FormData()
    params.append('userId', userId)

    const response = await api.put(`/news/like/dislike/${newsId}`, params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const reshoutNews = createAsyncThunk<
  News,
  { userId: string; newsId: string },
  { rejectValue: ValidationErrors }
>('news/reshout', async ({ userId, newsId }, { rejectWithValue }) => {
  try {
    const params = new FormData()
    params.append('userId', userId)

    const response = await api.put(`/news/retweet/${newsId}`, params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const getMediumPosts = createAsyncThunk('news/medium', async () => {
  try {
    const response = await axios.get(
      'https://v1.nocodeapi.com/syrname/medium/DxLmjWBZYFVymUMM',
    )
    return response.data as MediumPost[]
  } catch (err) {
    console.log('ERR - ', err)
  }
})

export const commentNews = createAsyncThunk<
  any,
  CommentParams,
  { rejectValue: ValidationErrors }
>(
  'news/comment/create',
  async ({ newsId, comment, cmtFile, parentCommentId }, { rejectWithValue }) => {
    try {
      const params = new FormData()
      if (cmtFile) params.append('image', cmtFile)
      if (comment) params.append('comment', comment)
      params.append('news_id', newsId)
      if (parentCommentId) params.append('parent_comment_id', parentCommentId)

      const response = await api.post('/create/comment', params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const createNewsForGame = createAsyncThunk<
  News,
  NewsParams,
  { rejectValue: ValidationErrors }
>(
  'news/createForGame',
  async ({ image, title, body, gameId }, { rejectWithValue }) => {
    try {
      const params = new FormData()
      if (image) params.append('image', image)
      if (title) params.append('title', title)
      params.append('body', body)

      const response = await api.post(`/create/news/${gameId}`, params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

// Create Quote Shout (share with comment)
export interface QuoteShoutParams {
  body: string
  quotedPostId: string
}

export const createQuoteShout = createAsyncThunk<
  News,
  QuoteShoutParams,
  { rejectValue: ValidationErrors }
>('news/createQuoteShout', async ({ body, quotedPostId }, { rejectWithValue }) => {
  try {
    const response = await api.post('/news/quote-shout', {
      body,
      quotedPostId,
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

// router.route("/news/game/:gameId").get(getNewsByGameId);
export const getNewsByGameId = createAsyncThunk<
  News[],
  string,
  { rejectValue: ValidationErrors }
>('news/getNewsByGameId', async (gameId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/news/game/${gameId}`)
    return response.data.allNews
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const deleteComment = createAsyncThunk<
  void,
  { newsId: string; commentId: string },
  { rejectValue: ValidationErrors }
>('news/deleteComment', async (commentId, { rejectWithValue }) => {
  try {
    console.log('commentId', commentId)

    await api.delete(`/admin/news/comment/${commentId.commentId}`)
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

// Like/unlike comment
export const likeComment = createAsyncThunk<
  { liked: boolean; likeCount: number },
  string,
  { rejectValue: ValidationErrors }
>('news/comment/like', async (commentId, { rejectWithValue }) => {
  try {
    const response = await api.post(`/comment/${commentId}/like`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// Get comment replies
export const getCommentReplies = createAsyncThunk<
  any[],
  string,
  { rejectValue: ValidationErrors }
>('news/comment/replies', async (commentId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/comment/${commentId}/replies`)
    return response.data.replies
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// Team Shouts
export const createTeamShout = createAsyncThunk<
  any,
  { teamId: string; title: string; body: string; image?: File; poll?: { options: string[]; duration?: number } | null },
  { rejectValue: ValidationErrors }
>('news/createTeamShout', async ({ teamId, title, body, image, poll }, { rejectWithValue }) => {
  try {
    const params = new FormData()
    params.append('team_id', teamId)
    params.append('title', title)
    params.append('body', body)
    if (image) params.append('image', image)
    // Same wire format as createNews — callers were already passing poll, but
    // the thunk silently dropped it and shout polls never reached the backend.
    if (poll) params.append('poll', JSON.stringify(poll))

    const response = await api.post('/team/shout', params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

export const getTeamShouts = createAsyncThunk<
  any,
  { teamId: string; page?: number; limit?: number },
  { rejectValue: ValidationErrors }
>('news/getTeamShouts', async ({ teamId, page = 1, limit = 20 }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/team/${teamId}/shouts`, {
      params: { page, limit }
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// Game Shouts
export const createGameShout = createAsyncThunk<
  any,
  { gameId: string; title: string; body: string; image?: File; poll?: { options: string[]; duration?: number } | null },
  { rejectValue: ValidationErrors }
>('news/createGameShout', async ({ gameId, title, body, image, poll }, { rejectWithValue }) => {
  try {
    const params = new FormData()
    params.append('game_owner_id', gameId)
    params.append('title', title)
    params.append('body', body)
    if (image) params.append('image', image)
    // Same wire format as createNews — callers were already passing poll, but
    // the thunk silently dropped it and shout polls never reached the backend.
    if (poll) params.append('poll', JSON.stringify(poll))

    const response = await api.post('/game/shout', params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

export const getGameShouts = createAsyncThunk<
  any,
  { gameId: string; page?: number; limit?: number },
  { rejectValue: ValidationErrors }
>('news/getGameShouts', async ({ gameId, page = 1, limit = 20 }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/game/${gameId}/shouts`, {
      params: { page, limit }
    })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// comments
